import { useEffect, useRef, useState } from 'react';

/**
 * Drop-in substituto de `useState` que grava o valor em `sessionStorage`.
 * Sobrevive a desmontagem/remontagem do componente (trocar de tela e voltar)
 * mas reseta ao fechar a aba/recarregar a página — não é persistência
 * permanente, só "lembrar onde parei" dentro da mesma sessão de navegação.
 *
 * `key` deve ser único por tela+campo (ex: 'boards:viewMode') para não
 * colidir entre páginas diferentes que usem o hook.
 *
 * Instâncias com a mesma chave ficam em sincronia: gravar ou apagar o valor
 * por fora (`writePersisted` / `clearPersisted`, ex.: fechar uma aba da barra
 * de abertos) atualiza quem estiver montado.
 */
interface PersistedStateOptions<T> {
  // Para tipos que não sobrevivem a JSON.stringify/parse direto (ex: Set) —
  // padrão é JSON puro.
  serialize?: (value: T) => string;
  deserialize?: (raw: string) => T;
}

/** Evento de mudança de um valor guardado — `raw` null = apagado. */
export const PERSISTED_EVENT = 'sophia:persisted-changed';
interface PersistedChange { storageKey: string; raw: string | null; origin?: symbol }

function announce(change: PersistedChange): void {
  // Fora do render: o setState de quem ouve não pode rodar durante o render de outro.
  queueMicrotask(() => window.dispatchEvent(new CustomEvent<PersistedChange>(PERSISTED_EVENT, { detail: change })));
}

/** Grava um valor por fora do componente (mesmo formato JSON do hook). */
export function writePersisted<T>(key: string, value: T): void {
  const storageKey = `sophia:${key}`;
  try {
    const raw = JSON.stringify(value);
    sessionStorage.setItem(storageKey, raw);
    announce({ storageKey, raw });
  } catch { /* ignora */ }
}

/** Apaga tudo o que foi guardado sob um prefixo (ex.: o rascunho de um formulário). */
export function clearPersisted(prefix: string): void {
  try {
    const full = `sophia:${prefix}`;
    Object.keys(sessionStorage).filter((k) => k.startsWith(full)).forEach((k) => {
      sessionStorage.removeItem(k);
      announce({ storageKey: k, raw: null });
    });
  } catch { /* ignora */ }
}

/** `key` null = `useState` comum (nada é guardado) — para componentes que só às
 *  vezes precisam lembrar o valor. */
export function usePersistedState<T>(key: string | null, initialValue: T, options?: PersistedStateOptions<T>) {
  const storageKey = key === null ? null : `sophia:${key}`;
  const serialize = options?.serialize ?? ((value: T) => JSON.stringify(value));
  const deserialize = options?.deserialize ?? ((raw: string) => JSON.parse(raw) as T);
  const originRef = useRef(Symbol('persisted'));
  const initialRef = useRef(initialValue);
  const deserializeRef = useRef(deserialize);
  deserializeRef.current = deserialize;

  const [state, setState] = useState<T>(() => {
    if (storageKey === null) return initialValue;
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored === null) return initialValue;
      return deserialize(stored);
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    if (storageKey === null) return;
    const onChange = (e: Event) => {
      const change = (e as CustomEvent<PersistedChange>).detail;
      if (change.storageKey !== storageKey || change.origin === originRef.current) return;
      try {
        setState(change.raw === null ? initialRef.current : deserializeRef.current(change.raw));
      } catch { /* valor ilegível — mantém o atual */ }
    };
    window.addEventListener(PERSISTED_EVENT, onChange);
    return () => window.removeEventListener(PERSISTED_EVENT, onChange);
  }, [storageKey]);

  const setPersistedState = (value: T | ((prev: T) => T)) => {
    setState((prev) => {
      const next = typeof value === 'function' ? (value as (prev: T) => T)(prev) : value;
      if (storageKey === null) return next;
      try {
        const raw = serialize(next);
        if (sessionStorage.getItem(storageKey) !== raw) {
          sessionStorage.setItem(storageKey, raw);
          announce({ storageKey, raw, origin: originRef.current });
        }
      } catch {
        // sessionStorage indisponível (modo privado/quota) — degrada para useState comum
      }
      return next;
    });
  };

  return [state, setPersistedState] as const;
}
